import {z} from 'zod';

import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Path segments
// ============================================================================

/**
 * Firestore collection path for the pool of Zoom accounts used by the TeachNow
 * zoom_allocator. Each document is keyed by `zoomId` and tracks allocation
 * state. See `src/apps/functions/src/zooms/zoom_allocator.ts` for the server
 * logic that allocates and frees these accounts.
 */
export const TEACHNOW_ZOOM_ACCOUNTS_COLLECTION_PATH =
  'zoomAllocator/teachnow/accounts' as const;

// ============================================================================
// Schemas
// ============================================================================

/**
 * A Zoom account in the TeachNow allocator pool. Mirrors the fields written by
 * `zoom_allocator.ts`. Transient allocation fields (`allocationId`,
 * `allocatedTime`, `expiryDurationMin`) are left loose since consumers of this
 * schema only inspect pool membership, not active allocation state.
 */
export const TeachnowZoomAccountSchema = z
  .object({
    /** The Zoom account id. Also the document id. */
    zoomId: z.string(),
    /** The email associated with the Zoom account. */
    email: z.string(),
    /** When this account was added to the TeachNow pool. */
    addedOn: FirestoreTimestampSchema,
    /**
     * Whether the account is part of the active production pool. Inactive
     * accounts are kept for debugging and should not count as conflicts.
     */
    isActive: z.boolean(),
    /**
     * Whether the account is currently allocated to a TeachNow room. Toggled
     * by the allocator transactions; not meaningful for conflict detection but
     * included so the schema matches the stored document shape.
     */
    inUse: z.boolean(),
  })
  .loose();

export type TeachnowZoomAccount = z.infer<typeof TeachnowZoomAccountSchema>;
